import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Tag, MapPin, Calendar, Clock, Shield, Gift, Check, AlertCircle } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { api } from '../services/api';
import { useAuth } from '../context/AuthContext';
import AuthModal from '../components/AuthModal';
import { toast } from 'sonner';
import { resolveBackendImage } from '../utils/brandLogo';

export default function CupomPage() {
  const { codigo } = useParams();
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [coupon, setCoupon] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reserving, setReserving] = useState(false);
  const [voucher, setVoucher] = useState(null);
  const [error, setError] = useState(null);
  const [showAuth, setShowAuth] = useState(false);

  useEffect(() => {
    api.get(`/v2/vouchers/coupon/${codigo}`)
      .then(res => setCoupon(res.data))
      .catch(() => setError('Cupom não encontrado'))
      .finally(() => setLoading(false));
  }, [codigo]);

  const handleReserve = async () => {
    if (!user && !token) {
      setShowAuth(true);
      return;
    }
    setReserving(true);
    try {
      const res = await api.post('/v2/vouchers/reserve', { coupon_codigo: codigo });
      setVoucher(res.data);
      toast.success('Cupom resgatado! Mostre o código ao lojista.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao resgatar cupom');
    } finally {
      setReserving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="w-8 h-8 border-3 border-purple-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error || !coupon) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <Card className="max-w-md w-full text-center py-12">
          <CardContent>
            <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-gray-800 mb-2">Cupom não encontrado</h2>
            <p className="text-sm text-gray-500">O código informado não corresponde a nenhum cupom ativo.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const isExpired = coupon.data_fim && new Date(coupon.data_fim) < new Date();
  const isFull = coupon.max_usos > 0 && coupon.total_usos >= coupon.max_usos;

  const formatDiscount = () => {
    if (coupon.tipo_desconto === 'percentual') return `${coupon.valor_desconto}%`;
    if (coupon.tipo_desconto === 'fixo') return `R$ ${coupon.valor_desconto}`;
    return 'Brinde';
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-50 to-white flex items-start justify-center px-4 py-8 sm:py-16" data-testid="cupom-page">
      <div className="max-w-md w-full space-y-4">
        {/* Business header */}
        <div className="text-center">
          {coupon.business?.imagem && (
            <img src={resolveBackendImage(coupon.business.imagem)} alt="" className="w-16 h-16 rounded-full mx-auto mb-3 object-cover border-2 border-white shadow" />
          )}
          <h2 className="text-sm font-medium text-gray-600">{coupon.business?.nome}</h2>
          {coupon.business?.bairro && (
            <p className="text-xs text-gray-400 flex items-center justify-center gap-1 mt-0.5">
              <MapPin className="w-3 h-3" /> {coupon.business.bairro}
            </p>
          )}
        </div>

        {/* Coupon card */}
        <Card className="border-2 border-dashed border-amber-300 overflow-hidden" data-testid="cupom-card">
          <div className="bg-gradient-to-r from-amber-50 to-yellow-50 px-6 py-5 text-center">
            <p className="text-3xl font-black text-amber-600" data-testid="cupom-desconto">
              {formatDiscount()} OFF
            </p>
            <h1 className="text-lg font-bold text-gray-900 mt-1" data-testid="cupom-titulo">{coupon.titulo}</h1>
          </div>
          <CardContent className="p-5 space-y-4">
            {coupon.descricao && (
              <p className="text-sm text-gray-600">{coupon.descricao}</p>
            )}

            <div className="flex flex-wrap gap-2">
              <Badge variant="outline" className="text-xs gap-1">
                <Tag className="w-3 h-3" /> {coupon.codigo}
              </Badge>
              {coupon.data_fim && (
                <Badge variant="outline" className={`text-xs gap-1 ${isExpired ? 'text-red-600 border-red-200' : ''}`}>
                  <Calendar className="w-3 h-3" />
                  {isExpired ? 'Expirado' : `Até ${new Date(coupon.data_fim).toLocaleDateString('pt-BR')}`}
                </Badge>
              )}
              {coupon.valor_minimo > 0 && (
                <Badge variant="outline" className="text-xs">
                  Mín. R$ {coupon.valor_minimo}
                </Badge>
              )}
            </div>

            {coupon.termos && (
              <p className="text-[11px] text-gray-400 italic">{coupon.termos}</p>
            )}
          </CardContent>
        </Card>

        {/* Actions */}
        {voucher ? (
          /* Voucher reserved - show QR */
          <Card className="border-green-200 bg-green-50" data-testid="voucher-reserved">
            <CardContent className="p-5 text-center space-y-4">
              <div className="flex items-center justify-center gap-2 text-green-700">
                <Check className="w-5 h-5" />
                <span className="font-bold text-sm">Cupom resgatado!</span>
              </div>
              <p className="text-xs text-green-600">Mostre este código ou QR Code ao lojista na hora da compra.</p>
              <div className="bg-white rounded-xl p-4 inline-block shadow-sm border">
                <QRCodeSVG
                  value={`${window.location.origin}/validar-voucher/${voucher.voucher_code}`}
                  size={160}
                />
              </div>
              <div className="bg-white rounded-lg px-4 py-3 border">
                <p className="text-[10px] text-gray-400 uppercase tracking-wide">Seu código</p>
                <p className="text-2xl font-black tracking-widest text-gray-900" data-testid="voucher-code">{voucher.voucher_code}</p>
              </div>
              <div className="flex items-center gap-2 justify-center text-xs text-gray-500">
                <Clock className="w-3.5 h-3.5" />
                <span>Válido até {new Date(voucher.expires_at).toLocaleDateString('pt-BR')} às {new Date(voucher.expires_at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}</span>
              </div>
              <Button variant="outline" size="sm" onClick={() => navigate('/perfil')} className="mt-2" data-testid="go-wallet-btn">
                <Gift className="w-4 h-4 mr-1.5" /> Ver minha carteira de cupons
              </Button>
            </CardContent>
          </Card>
        ) : (
          /* Reserve button */
          <div className="space-y-3">
            {isExpired ? (
              <Button disabled className="w-full" size="lg">Cupom expirado</Button>
            ) : isFull ? (
              <Button disabled className="w-full" size="lg">Cupom esgotado</Button>
            ) : (
              <Button
                className="w-full bg-[#56007F] hover:bg-[#420060] text-white"
                size="lg"
                onClick={handleReserve}
                disabled={reserving}
                data-testid="reserve-btn"
              >
                {reserving ? (
                  <span className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Resgatando...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <Gift className="w-5 h-5" /> Quero este Cupom
                  </span>
                )}
              </Button>
            )}
            {!user && !token && (
              <p className="text-xs text-center text-gray-400">
                Faça login para resgatar
              </p>
            )}
            <div className="flex items-center justify-center gap-1.5 text-[11px] text-gray-400">
              <Shield className="w-3.5 h-3.5" />
              <span>Voucher pessoal válido por 72h</span>
            </div>
          </div>
        )}
      </div>

      {/* Auth Modal */}
      <AuthModal
        isOpen={showAuth}
        onClose={() => setShowAuth(false)}
        onSuccess={handleReserve}
      />
    </div>
  );
}
